/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import { Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { ListView, ListViewRow } from 'model/state';
import { Alert, statusToString, Location, AlertStatus, isLocationFullAccess } from 'model';
import { FloCheckbox, FloList, StatusIndicator } from 'components';
import moment from 'moment-timezone';
import styles from './AlertList.module.css';
import classnames from 'classnames';

type AlertHeaderProps = {
  isSelectedAll:    boolean,
  setSelectAll:     (selectAll: boolean) => void,
};

const AlertHeader: React.FC<AlertHeaderProps> = ({isSelectedAll, setSelectAll}) => {
  const { t } = useTranslation();
  return (
    <>
      <Col xs="5" md="4" lg="4">
        <FloCheckbox checked={isSelectedAll} onChange={() => setSelectAll(!isSelectedAll)} />
        <span>{t('Alert')}</span>
      </Col>
      <Col        md="3" lg="4" className="d-none d-md-block">{t('Location')}</Col>
      <Col xs="3" md="2" lg="2">{t('Date')}</Col>
      <Col xs="4" md="3" lg="2">{t('Status')}</Col>
    </>
  )
}

type AlertRowProps = {
  alert:          ListViewRow<Alert>,
  setSelectRow:   (select: boolean, alertId: string) => void,
  onSelect:       (alert: Alert) => void,
}

const AlertRow: React.FC<AlertRowProps> = React.memo(({alert, setSelectRow, onSelect}) => {
  const location = (alert.location as Location).address ? alert.location as Location : undefined;

  return (
    <>
      <Col xs="5" md="4" lg="4" className={styles.firstColumn} >
        <div className={styles.alarmCheckBoxWrapper}>
          <FloCheckbox checked={alert.isSelected} noMargin onChange={() => setSelectRow(!alert.isSelected, alert.id || '')} />
        </div>
        <div className={styles.alarmTitleContainer}>
          <StatusIndicator className={styles.statusIndicator} severity={alert.alarm.severity} /><span onClick={() => onSelect(alert)}>{alert.displayTitle}</span>
        </div>
      </Col>
      <Col        md="3" lg="4" className="d-none d-md-block">
        <a className={styles.mainText} href={`/unit/${location?.id}`} target="_blank" rel="noopener noreferrer">
          <div className={styles.mainText}>{location?.nickname}</div>
          <div> 
            <span className={styles.locationAddress}>{`${location?.address}, `}</span>
            <span className={styles.parentName}>{location?.city ? `${location?.city}, ${location?.state?.toUpperCase()}` : ''}</span>
          </div>
        </a>
      </Col>
      <Col        md="2" lg="2" className="d-none d-md-block">{moment.utc(alert.createAt).tz(location?.timezone || 'US/Pacific').format('LLL')}</Col>
      <Col xs="3"               className={classnames("d-block", "d-md-none", styles.reducedSize)}>{moment.utc(alert.createAt).tz(location?.timezone || 'US/Pacific').format('L LT')}</Col>
      <Col xs="4" md="3" lg="2" className={styles.reducedSize}>
        <StatusColumn alertStatus={alert.status} location={location} onSelectButton={() => onSelect(alert)} />
      </Col>
    </>
  )
})

type StatusColumnProps = {
  alertStatus:    AlertStatus,
  onSelectButton: () => void,
  location:       Location | undefined;
};

const StatusColumn: React.FC<StatusColumnProps> = React.memo(({alertStatus, onSelectButton, location}) => {
  const { t } = useTranslation();
  const handleSelectButton = () => {
    if (alertStatus === AlertStatus.TRIGGERED && isLocationFullAccess(location)) {
      onSelectButton();
    }
  };
  return (
    <div
      className = {classnames(
        styles.statusInfo,
        { [styles.resolveButton]: alertStatus === AlertStatus.TRIGGERED && isLocationFullAccess(location)}
      )}
      onClick = {handleSelectButton}
    >
      <svg className={classnames({[styles.statusIconHidden]: alertStatus === AlertStatus.TRIGGERED})} width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M8 0C3.6 0 0 3.6 0 8C0 12.4 3.6 16 8 16C12.4 16 16 12.4 16 8C16 3.6 12.4 0 8 0ZM7 11.4L3.6 8L5 6.6L7 8.6L11 4.6L12.4 6L7 11.4Z" fill="#00D870"/>
      </svg>
      <span className={styles.statusText}>{t(statusToString(alertStatus))}</span>
      <span className={styles.markResolved}>{t('Mark Resolved')}</span>
    </div>
  )
})

type AlertListProps = {
  alertList:     ListView<Alert>,
  isSelectedAll: boolean,
  setSelectAll:  (selectAll: boolean) => void,
  setSelectRow:  (select: boolean, alertId: string) => void,
  onSelect:      (alert: Alert) => void,
}

export const AlertList: React.FC<AlertListProps> = React.memo(({
  alertList,
  isSelectedAll,
  setSelectAll,
  setSelectRow,
  onSelect
}) => {
  return (
    <FloList>
      <FloList.Header>
        <AlertHeader isSelectedAll={isSelectedAll} setSelectAll={setSelectAll} />
      </FloList.Header>
      <FloList.Body>
          { alertList.rows?.map((alert) => (
            <FloList.Row key={`alert-row-${alert.id}`} className={styles.alertRow}>
              <AlertRow alert={alert} setSelectRow={setSelectRow} onSelect={onSelect} />
            </FloList.Row>
          ))}
      </FloList.Body>
    </FloList>
  )
})
